import { useEffect, useLayoutEffect, useRef } from 'react'
import type { PageTypeResult } from 'src/hooks/usePageType'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { useElementOnScreen } from 'src/hooks/useElementOnScreen'

interface SphereContentProps {
  html: string
  pageData: PageTypeResult
}

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect

export function SphereContent({ html, pageData }: SphereContentProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { containerRef, isVisible, hasBeenViewed, setHasBeenViewed } =
    useElementOnScreen({ rootMargin: '200px' })

  useIsomorphicLayoutEffect(() => {
    if (!isVisible || hasBeenViewed) {
      return
    }

    const el = ref.current

    if (!el) {
      return
    }

    const clone = el.cloneNode(true) as HTMLElement

    const scripts = clone.querySelectorAll('script')

    scripts.forEach((oldScript) => {
      const newScript = document.createElement('script')

      Array.from(oldScript.attributes).forEach((attr) =>
        newScript.setAttribute(attr.name, attr.value)
      )
      newScript.text = oldScript.text
      oldScript.replaceWith(newScript)
    })

    el.replaceWith(clone)
    setHasBeenViewed(true)
  }, [isVisible, hasBeenViewed, setHasBeenViewed])

  const sendSphereComponent = () => {
    const standardObject = getStandardObject(pageData)

    dispatchAmplitudeEvent({
      eventName: 'Product Information Selected',
      eventData: {
        ...standardObject,
        'information tab name': 'Sphere',
      },
    })
  }

  if (!html) {
    return null
  }

  return (
    <>
      <div
        ref={containerRef}
        className="relative isolate overflow-hidden pt-10"
        style={{ transform: 'translateZ(0)' }}
        onClick={sendSphereComponent}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            sendSphereComponent()
          }
        }}
      >
        <div
          ref={ref}
          id="sphere-content"
          className="w-full max-w-[1280px] px-4 restructure-tablet:px-5 restructure-small-desktop:px-0 mx-auto"
          dangerouslySetInnerHTML={{ __html: decodeURIComponent(html) }}
          suppressHydrationWarning
        />
      </div>
    </>
  )
}
